import React, { useState, memo } from 'react';
import cc from 'classcat';
import { useTimeout } from 'usehooks-ts';
import type { Params } from 'react-container-query/lib/interfaces';
import { useContainerQuery } from 'react-container-query';
import GameInfo from 'components/GameInfo';
import { useAppSelector } from 'hooks/redux';
import Slot from 'modules/Slot';
import type { Metadata } from 'lib/metadata';
import { TypographyContext, TypographyVariant } from 'components/Typography';
import { GameVariant } from './constants';
import './gameLoadingScreen.css';

const GAME_LOGO_DELAY = 5 * 1000;
const GAME_INFO_DELAY = 400;

const cQuery = {
  lg: { minHeight: 400, minWidth: 800 },
  md: { minHeight: 320 },
  xs: { minHeight: 0 }
};

const TypographyClasses: Record<string, TypographyContext> = {
  lg: { rtpLabel: TypographyVariant.BodyXlStrong, rtpValue: TypographyVariant.HeaderXl },
  md: { rtpLabel: TypographyVariant.BodySmStrong, rtpValue: TypographyVariant.HeaderSm },
  xs: { rtpLabel: TypographyVariant.BodyXxsStrong, rtpValue: TypographyVariant.HeaderXxs }
};

export const getCurrentSize = (params: Params) =>
  // Relying on useContainerQuery to return params in the same order as cQuery which is true so far.
  // If that would not be the case we would need to sort the keys by minWidth using cQuery.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we know it can't be null
  Object.keys(params).find((item) => params[item])!;

interface GameLoadingScreenProps {
  logoUrl?: string;
  isActive?: boolean;
  gameRef: string;
  gameVariant?: GameVariant;
}

const GameLoadingScreen = ({ logoUrl, isActive, gameRef, gameVariant }: GameLoadingScreenProps) => {
  const metadata = useAppSelector((state) => {
    if (gameVariant === GameVariant.Bingo) {
      // when used in Bingo.
      // Bingo metadata is not implemented yet, so return undefined
      return undefined;
    }
    // when used in Slot/Live
    return Slot.selectors.getMetadata(state, gameRef) as Metadata;
  });
  const [date] = useState(Date.now);
  const [shouldShowGameLogo, setShouldShowGameLogo] = useState(false);
  const [shouldShowGameInfo, setShouldShowGameInfo] = useState(false);
  const [params, containerRef] = useContainerQuery(cQuery, {});
  const currentSize = getCurrentSize(params);

  const className = cc({
    gameLoadingScreen: true,
    'gameLoadingScreen--hasMetadata': !!metadata?.rtp?.formattedValue,
    'gameLoadingScreen--showGameLogo': shouldShowGameLogo,
    'gameLoadingScreen--hasEnded': !isActive,
    'gameLoadingScreen--xs': params.xs,
    'gameLoadingScreen--md': params.md,
    'gameLoadingScreen--lg': params.lg
  });

  useTimeout(() => {
    setShouldShowGameLogo(true);
  }, GAME_LOGO_DELAY);

  useTimeout(() => {
    setShouldShowGameInfo(true);
  }, GAME_INFO_DELAY);

  return (
    <div className={className} ref={containerRef}>
      <TypographyContext.Provider value={TypographyClasses[currentSize]}>
        <div className="gameLoadingScreen__logoContainer" data-chromatic="ignore">
          <img
            className="gameLoadingScreen__mrqLogo"
            src={`/assets/images1/mrq-presents.gif?cacheBust=${date}`}
            alt="MrQ Logo"
          />
          <div className="gameLoadingScreen__gameLogoContainer">
            <img className="gameLoadingScreen__gameLogo pulse" src={logoUrl} alt="" />
          </div>
        </div>
        <GameInfo metadata={metadata} isActive={shouldShowGameInfo} />
      </TypographyContext.Provider>
    </div>
  );
};

export default memo(GameLoadingScreen);
